import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTags from '../hooks/useTags';
import { apiCall } from '../utils/api';
import useUserPermissions from '../hooks/useUserPermissions';
import { reorderStep } from '../utils/stepReorder';
import config from '../config/appConfig';

function EditRunbook() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { canModify, isLoading } = useUserPermissions();
  
  const [runbook, setRunbook] = useState(null);
  const [editedRunbook, setEditedRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [highlightedStep, setHighlightedStep] = useState(null);

  const { selectedTags, setSelectedTags, tagInput, suggestions, tagInputRef, suggestionBoxRef, addTag, removeTag, handleTagInputChange, handleTagInputKeyPress, handleTagInputBlur } = useTags();

  // Redirect users without modify permissions
  useEffect(() => {
    if (!isLoading && !canModify()) {
      alert('You do not have permission to edit runbooks. Redirecting to view mode.');
      navigate(`/view-runbook/${id}`);
      return;
    }
  }, [canModify, isLoading, navigate, id]);

  useEffect(() => {
    const fetchRunbookData = async () => {
      try {
        setLoading(true);
        const data = await apiCall(`/runbooks/${id}`);
        
        setRunbook(data);
        setEditedRunbook(JSON.parse(JSON.stringify(data))); // Deep copy
        setSelectedTags(data.Tags || []);
        
        // Extract existing image URLs from steps
        const existingImages = {};
        if (data.Steps) {
          data.Steps.forEach(step => {
            if (step.image_url) {
              existingImages[step.step_number] = step.image_url;
            }
          });
        }
        setImageUrls(existingImages);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching runbook data:', error);
        setError(error.message || 'Failed to load runbook');
        setLoading(false);
      }
    };
    fetchRunbookData();
  }, [id, setSelectedTags]);

  const handleInputChange = (field, value) => {
    setEditedRunbook(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStepChange = (index, field, value) => {
    // Ensure Steps array exists
    const currentSteps = editedRunbook.Steps || [];
    const newSteps = [...currentSteps];
    newSteps[index] = {
      ...newSteps[index],
      [field]: field === 'step_number' ? parseInt(value) || 0 : value
    };
    setEditedRunbook(prev => ({
      ...prev,
      Steps: newSteps
    }));
  };

  const handleAddStep = () => {
    // Initialize Steps array if it doesn't exist
    const currentSteps = editedRunbook.Steps || [];
    const newStepNumber = currentSteps.length + 1;
    const newSteps = [
      ...currentSteps,
      {
        step_number: newStepNumber,
        step_description: ''
      }
    ];
    setEditedRunbook(prev => ({
      ...prev,
      Steps: newSteps
    }));
  };

  const handleDeleteStep = (index) => {
    if (!window.confirm('Are you sure you want to delete this step?')) {
      return;
    }

    // Ensure Steps array exists
    const currentSteps = editedRunbook.Steps || [];
    if (currentSteps.length === 0) return;
    
    // Remove the step from array
    const newSteps = currentSteps.filter((_, idx) => idx !== index);
    
    // Renumber steps in frontend state only
    const renumberedSteps = newSteps.map((step, idx) => ({
      ...step,
      step_number: idx + 1
    }));
    
    // Update image URLs state to match new step numbers
    const updatedImageUrls = {};
    renumberedSteps.forEach((step, idx) => {
      const oldStepNumber = currentSteps[idx < index ? idx : idx + 1].step_number;
      if (imageUrls[oldStepNumber]) {
        updatedImageUrls[idx + 1] = imageUrls[oldStepNumber];
      }
    });
    
    setImageUrls(updatedImageUrls);
    
    // Update edited runbook with renumbered steps
    // Image S3 keys remain unchanged (still pointing to temp location)
    // Backend will handle S3 cleanup and renumbering on save
    setEditedRunbook(prev => ({
      ...prev,
      Steps: renumberedSteps
    }));
    
    console.log('Step deleted in frontend. S3 changes will be applied on save.');
  };

  const handleMoveStep = (index, direction) => {
    const currentSteps = editedRunbook.Steps || [];
    const result = reorderStep(currentSteps, imageUrls, index, direction);
    if (!result) return;

    setImageUrls(result.imageUrls);
    setEditedRunbook(prev => ({
      ...prev,
      Steps: result.steps
    }));

    // Highlight the moved step at its new position
    setHighlightedStep(result.newIndex + 1);
    setTimeout(() => setHighlightedStep(null), 1200);

    console.log(`[REORDER] Moved step from position ${index + 1} ${direction} to position ${result.newIndex + 1}`);
  };

  const handleImageUpload = async (stepNumber, file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, BMP, WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, [stepNumber]: true }));

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        try {
          const base64Data = reader.result.split(',')[1];

          const response = await apiCall('/runbooks/upload-step-image', {
            method: 'POST',
            body: JSON.stringify({
              runbookId: editedRunbook['Runbook ID'],
              tribe: editedRunbook.Tribe,
              solution: editedRunbook.Solution,
              stepNumber: stepNumber,
              image: base64Data,
              fileName: file.name,
              isTemp: true  // Flag for temporary upload during editing
            })
          });

          if (response.imageUrl) {
            setImageUrls(prev => ({ ...prev, [stepNumber]: response.imageUrl }));
            
            // Update the step with BOTH image_s3_key and image_url
            const currentSteps = editedRunbook.Steps || [];
            const newSteps = currentSteps.map(step => {
              if (step.step_number === stepNumber) {
                return { 
                  ...step, 
                  image_s3_key: response.s3Key,      // Store S3 key for DynamoDB
                  image_url: response.imageUrl        // Store presigned URL for display
                };
              }
              return step;
            });
            
            setEditedRunbook(prev => ({
              ...prev,
              Steps: newSteps
            }));

            alert('Image uploaded successfully!');
          }
        } catch (error) {
          console.error('Upload error:', error);
          alert('Failed to upload image: ' + (error.message || 'Unknown error'));
        } finally {
          setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
        }
      };

      reader.onerror = () => {
        alert('Failed to read file');
        setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
      };
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image: ' + (error.message || 'Unknown error'));
      setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const payload = {
      runbookTitle: editedRunbook['Runbook Title'],
      // modifiedBy will be set automatically by backend from JWT token
      selectedTags: Array.from(selectedTags),
      Steps: editedRunbook.Steps  // Backend will move temp images (Runbooks/temp/*) to permanent location
    };
    
    console.log('[EDIT] Submitting runbook update...', payload);
    console.log('[EDIT] Steps being sent:', payload.Steps?.length || 0);
    payload.Steps?.forEach((step, idx) => {
      console.log(`[EDIT] Step ${step.step_number}:`, {
        description: step.step_description?.substring(0, 50),
        has_image: !!step.image_s3_key,
        s3_key: step.image_s3_key || 'none'
      });
    });
    
    try {
      setSaving(true);
      const response = await apiCall(`/runbooks/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('[EDIT] Update successful:', response);
      alert('Runbook updated successfully!');
      navigate('/runbook_list');
    } catch (error) {
      console.error('[EDIT] Error updating runbook:', error);
      console.error('[EDIT] Error message:', error.message);
      console.error('[EDIT] Error details:', error.details);
      
      // Log details array if it exists
      if (error.details && Array.isArray(error.details)) {
        console.error('[EDIT] Validation errors:', JSON.stringify(error.details, null, 2));
        error.details.forEach((detail, idx) => {
          console.error(`[EDIT] Error ${idx + 1}:`, detail);
        });
      }
      
      // Show detailed error message
      let errorMessage = 'Failed to update runbook:\n\n';
      errorMessage += error.message || 'Unknown error';
      
      if (error.details) {
        errorMessage += '\n\nValidation errors:';
        if (Array.isArray(error.details)) {
          error.details.forEach(detail => {
            errorMessage += '\n• ' + detail;
          });
        } else if (typeof error.details === 'object') {
          errorMessage += '\n' + JSON.stringify(error.details, null, 2);
        } else {
          errorMessage += '\n' + error.details;
        }
      }
      
      alert(errorMessage);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Warn user about unsaved changes
    const hasUnsavedChanges = JSON.stringify(runbook) !== JSON.stringify(editedRunbook);
    
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm(
        'You have unsaved changes. Any uploaded images will remain in temporary storage. Are you sure you want to cancel?'
      );
      
      if (!confirmCancel) {
        return;
      }
    }
    
    // Note: Temp images in Runbooks/temp/{runbookId}/ will be cleaned up by periodic cleanup
    // or will be overwritten on next successful save
    navigate('/runbook_list');
  };

  if (loading || isLoading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading runbook...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  if (!editedRunbook) {
    return <div className="container-fluid mt-4"><p>Runbook not found</p></div>;
  }

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Runbook</h2>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/runbook_list')}
          disabled={saving}
        >
          Back to Runbook List
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="card mb-4">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">Basic Information</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label"><strong>Runbook ID</strong></label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={editedRunbook['Runbook ID'] || ''}
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <label className="form-label"><strong>Modified By</strong></label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value="Logged in User"
                  readOnly
                />
                <small className="text-muted">Automatically set on save</small>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label"><strong>Tribe</strong></label>
                <select
                  className="form-control"
                  value={editedRunbook.Tribe || ''}
                  onChange={(e) => {
                    handleInputChange('Tribe', e.target.value);
                    // Reset Solution when Tribe changes
                    handleInputChange('Solution', '');
                  }}
                >
                  <option value="">Select Tribe</option>
                  {config.tribes.map((tribe) => (
                    <option key={tribe} value={tribe}>
                      {tribe}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label"><strong>Solution</strong></label>
                <select
                  className="form-control"
                  value={editedRunbook.Solution || ''}
                  onChange={(e) => handleInputChange('Solution', e.target.value)}
                  disabled={!editedRunbook.Tribe}
                >
                  <option value="">Select Solution</option>
                  {editedRunbook.Tribe && config.solutions[editedRunbook.Tribe] &&
                    Object.keys(config.solutions[editedRunbook.Tribe]).map((solutionName) => (
                      <option key={solutionName} value={solutionName}>
                        {solutionName}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Runbook Title <span className="text-danger">*</span></strong></label>
              <input
                type="text"
                className="form-control"
                value={editedRunbook['Runbook Title'] || ''}
                onChange={(e) => handleInputChange('Runbook Title', e.target.value)}
                maxLength={256}
                required
              />
              <small className="text-muted">
                {(editedRunbook['Runbook Title'] || '').length}/256 characters
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Tags</strong></label>
              <div id="selected-tags" className="tag-container mb-2">
                {selectedTags && Array.from(selectedTags).map((tag) => (
                  <span key={tag} className="tag badge bg-primary me-2 mb-2">
                    {tag} <span className="remove-tag" onClick={() => removeTag(tag)} style={{ cursor: 'pointer', marginLeft: '5px' }}>✖</span>
                  </span>
                ))}
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Enter tags"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyPress={handleTagInputKeyPress}
                onBlur={handleTagInputBlur}
                ref={tagInputRef}
              />
              {suggestions && suggestions.length > 0 && (
                <div className="suggestions border rounded mt-1 bg-white position-absolute" ref={suggestionBoxRef} style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="suggestion-item p-2"
                      onClick={() => addTag(suggestion)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
              <small className="text-muted">Separate tags with commas or Enter</small>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="card mb-4">
          <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Resolution Steps ({editedRunbook.Steps?.length || 0})</h5>
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={handleAddStep}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Step
            </button>
          </div>
          <div className="card-body">
            {editedRunbook.Steps && editedRunbook.Steps.length > 0 ? (
              editedRunbook.Steps.map((step, index) => (
                <div
                  key={index}
                  className={`card mb-3 step-card-reorderable ${highlightedStep === step.step_number ? 'step-card-highlight' : ''}`}
                >
                  <div className="card-body">
                    <div className="row mb-2">
                      <div className="col-md-2">
                        <label className="form-label"><strong>Step #</strong></label>
                        <input
                          type="number"
                          className="form-control"
                          value={step.step_number}
                          disabled
                          min="1"
                        />
                      </div>
                      <div className="col-md-10">
                        <label className="form-label">
                          <strong>Description</strong>
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={step.step_description}
                          onChange={(e) => handleStepChange(index, 'step_description', e.target.value)}
                          placeholder="Enter step description"
                        />
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label className="form-label">
                          <strong>Step Image (Optional)</strong>
                        </label>
                        <div className="input-group">
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleImageUpload(step.step_number, file);
                              }
                            }}
                            disabled={uploadingImages[step.step_number]}
                          />
                          {uploadingImages[step.step_number] && (
                            <span className="input-group-text">
                              <span className="spinner-border spinner-border-sm"></span>
                            </span>
                          )}
                        </div>
                        <small className="text-muted d-block mt-1">
                          Upload path: Runbooks/{editedRunbook.Tribe}/{editedRunbook.Solution}/{editedRunbook['Runbook ID']}/step_{step.step_number}/
                        </small>
                        {uploadingImages[step.step_number] && (
                          <div className="alert alert-info mt-2 mb-0">
                            <i className="bi bi-cloud-upload me-2"></i>
                            Uploading image...
                          </div>
                        )}
                        {imageUrls[step.step_number] && (
                          <div className="alert alert-success mt-2 mb-0">
                            <i className="bi bi-check-circle me-2"></i>
                            Image uploaded successfully!
                            <div className="mt-3 mb-3">
                              <img 
                                src={imageUrls[step.step_number]} 
                                alt={`Step ${step.step_number}`}
                                className="img-fluid rounded border"
                                style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                              />
                            </div>
                            <a 
                              href={imageUrls[step.step_number]} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              <i className="bi bi-box-arrow-up-right me-1"></i>
                              View Full Size
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="step-actions-row">
                      <div className="step-reorder-btn-group">
                        <button
                          type="button"
                          className="step-reorder-btn"
                          title="Move step up"
                          onClick={() => handleMoveStep(index, 'up')}
                          disabled={index === 0}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="step-reorder-btn"
                          title="Move step down"
                          onClick={() => handleMoveStep(index, 'down')}
                          disabled={index === editedRunbook.Steps.length - 1}
                        >
                          ▼
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteStep(index)}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete Step
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="alert alert-warning">
                No steps defined. Click "Add Step" to create one.
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mb-4">
          {canModify() && (
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={saving || !editedRunbook.Steps || editedRunbook.Steps.length === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-1"></i>
                  Save Changes
                </>
              )}
            </button>
          )}
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCancel}
            disabled={saving}
          >
            {canModify() ? 'Cancel' : 'Back'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditRunbook;


